import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Heading,
  SimpleGrid,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { ExternalLink } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import { taskService } from '@/lib/services/taskService'
import { taskReportService, type TaskDashboardSummary } from '@/lib/services/taskReportService'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import TaskStatusPill from '@/components/helpdesk/TicketDetail/tasks/TaskStatusPill'
import TaskPriorityBadge from '@/components/helpdesk/TicketDetail/tasks/TaskPriorityBadge'

const PER_PAGE = 25

function formatDueAt(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

export default function MyTasksPage() {
  const [tasks, setTasks] = useState<ChildTicketTask[]>([])
  const [summary, setSummary] = useState<TaskDashboardSummary | null>(null)
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState<{ total: number; last_page: number; current_page: number } | null>(null)
  const [loadingTasks, setLoadingTasks] = useState(true)
  const [loadingSummary, setLoadingSummary] = useState(true)

  useEffect(() => {
    setLoadingSummary(true)
    taskReportService
      .getSummary('mine')
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoadingSummary(false))
  }, [])

  useEffect(() => {
    setLoadingTasks(true)
    taskService
      .listMine(page, PER_PAGE)
      .then((res) => {
        setTasks(res.data)
        setMeta(res.meta)
      })
      .catch(() => {
        setTasks([])
        setMeta(null)
      })
      .finally(() => setLoadingTasks(false))
  }, [page])

  return (
    <Box p={4}>
      <Heading size="md" mb={1}>
        My Tasks
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>
        Cross-ticket view of tasks currently assigned to you
      </Text>

      {/* Summary cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        {loadingSummary ? (
          [1, 2, 3, 4].map((i) => <Skeleton key={i} height="80px" borderRadius="md" />)
        ) : summary ? (
          <>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Total</StatLabel>
                  <StatNumber>{summary.total}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Open</StatLabel>
                  <StatNumber>{summary.open_count}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Overdue</StatLabel>
                  <StatNumber color="red.500">{summary.overdue_count}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <Stat>
                  <StatLabel>Due Today</StatLabel>
                  <StatNumber color="orange.500">{summary.due_today_count}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
          </>
        ) : null}
      </SimpleGrid>

      {/* Task list */}
      <Card>
        <CardBody overflowX="auto">
          {loadingTasks ? (
            <Skeleton height="240px" />
          ) : (
            <>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>#</Th>
                    <Th>Title</Th>
                    <Th>Parent Ticket</Th>
                    <Th>Status</Th>
                    <Th>Priority</Th>
                    <Th>Due</Th>
                    <Th aria-label="Actions" />
                  </Tr>
                </Thead>
                <Tbody>
                  {tasks.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" color="gray.400" py={10} fontSize="sm">
                        No tasks are currently assigned to you
                      </Td>
                    </Tr>
                  ) : (
                    tasks.map((task) => (
                      <Tr key={task.id}>
                        <Td fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
                          {task.task_number ?? `#${task.id}`}
                        </Td>
                        <Td maxW="220px">
                          <Text noOfLines={2} fontSize="sm">
                            {task.title ?? task.notes ?? '—'}
                          </Text>
                        </Td>
                        <Td>
                          <Button
                            as={RouterLink}
                            to={`/tickets/${task.parent_ticket_id}/view?task=${task.id}`}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            rightIcon={<ExternalLink size={10} />}
                          >
                            #{task.parent_ticket_id}
                          </Button>
                        </Td>
                        <Td>
                          <TaskStatusPill status={task.status ?? ''} statusLabel={task.status_label} />
                        </Td>
                        <Td>
                          <TaskPriorityBadge priority={task.priority ?? 'medium'} />
                        </Td>
                        <Td fontSize="xs" whiteSpace="nowrap">
                          {formatDueAt(task.due_at)}
                        </Td>
                        <Td>
                          <Button
                            as={RouterLink}
                            to={`/tickets/${task.parent_ticket_id}/view?task=${task.id}`}
                            size="xs"
                            variant="outline"
                            colorScheme="blue"
                            rightIcon={<ExternalLink size={10} />}
                          >
                            View
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>

              {meta && meta.last_page > 1 && (
                <HStack mt={4} justify="space-between" px={2}>
                  <Text fontSize="sm" color="gray.500">
                    Page {meta.current_page} of {meta.last_page} &bull; {meta.total} tasks
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      isDisabled={page <= 1}
                    >
                      Prev
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                      isDisabled={page >= meta.last_page}
                    >
                      Next
                    </Button>
                  </HStack>
                </HStack>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  )
}
